import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AccountLink } from "@/components/journey/AccountLink";
import { AnimatePresence, motion } from "framer-motion";
import { HeartHandshake, Sparkles, Ticket } from "lucide-react";
import { TrialOffer } from "@/components/discovery/TrialOffer";
import { useTrialEligibility } from "@/components/billing/TrialTerms";
import { PartnershipForm } from "@/components/journey/PartnershipForm";
import { InviteRedeem } from "@/components/journey/InviteRedeem";
import { startTrialCheckout } from "@/services/unsaidService";
import { track } from "@/lib/analytics";
import { LegalNotice } from "@/components/fionova/LegalNotice";

const PATHS = [
  { key: "subscribe", Icon: Sparkles, title: "Continue My Journey", desc: "For individuals. Ellie, Companion Memory and the full FioNova experience." },
  { key: "partner", Icon: HeartHandshake, title: "Explore a Professional Partnership", desc: "For therapists, recovery coaches, treatment facilities and other professionals supporting clients between sessions." },
  { key: "reviewer", Icon: Ticket, title: "Access My Complimentary Invitation", desc: "For invited reviewers, content creators and approved partners with an invitation code." },
];
const FEATURES = "Companion Memory, ongoing Ellie conversations, Daily Battle Plan, Emotional X-Ray, Future Self, Purpose Engine, a private Journal, Progress Tracker and other premium features.";

const Option = ({ p, active, onClick, extra }) => (
  <button data-testid={`path-${p.key}`} onClick={onClick} aria-pressed={active}
    className={`fio-glass fio-hairline flex w-full items-start gap-4 rounded-3xl p-5 text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 ${active ? "ring-2 ring-teal-500 shadow-[0_18px_40px_-20px_rgba(13,148,136,0.6)]" : ""}`}>
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500/15 to-violet-500/15 text-teal-700"><p.Icon className="h-5 w-5" /></span>
    <span><span className="block text-[16px] font-semibold text-slate-900">{p.title}</span><span className="mt-1 block text-sm leading-relaxed text-slate-600">{p.desc}</span>{extra}</span>
  </button>
);

// The three ending pathways after the closing reflection. Choosing one never discards the saved journey.
export const JourneyPaths = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [path, setPath] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const offer = useTrialEligibility();
  const choose = (k) => { setPath(k); track("journey_path_selected", { path: k }); };
  const checkout = async () => {
    setCheckingOut(true);
    track("stripe_checkout_started", { entry: "journey" });
    try {
      const { checkout_url } = await startTrialCheckout();
      if (checkout_url) window.location.href = checkout_url; else setCheckingOut(false);
    } catch { setCheckingOut(false); }
  };

  return (
    <section className="mt-14" data-testid="journey-paths">
      <h2 className="text-center text-[28px] font-bold leading-tight tracking-tight text-slate-900" data-testid="paths-headline">
        Your Journey Doesn't Have to <span className="fio-gradient-text">End Here.</span>
      </h2>
      <p className="mt-2 text-center text-[15px] text-slate-600" data-testid="paths-subheadline">Choose how you'd like to continue.</p>
      <div className="mt-7 space-y-3">
        {PATHS.map((p) => (
          <Option key={p.key} p={p} active={path === p.key} onClick={() => choose(p.key)}
            extra={p.key === "subscribe" && (
              <>
                {offer?.headline && <span className="mt-2 block text-[13.5px] font-semibold text-teal-800" data-testid="path-subscribe-price">{offer.headline}</span>}
                <span className="mt-1 block text-[12.5px] leading-relaxed text-slate-500">{FEATURES}</span>
              </>
            )} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {path && (
          <motion.div key={path} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
            {path === "subscribe" && (user ? <TrialOffer onStart={checkout} checkingOut={checkingOut} /> : (
              <div className="fio-glass-strong fio-hairline mt-8 rounded-[32px] p-7" data-testid="subscribe-account-step">
                <h2 className="text-xl font-bold tracking-tight text-slate-900">First, let's save your journey to your account</h2>
                <p className="mt-2 mb-5 text-[14.5px] leading-relaxed text-slate-600">Create a free account or sign in. Everything we explored moves into it, and then you'll see your exact pricing before secure checkout.</p>
                <AccountLink onDone={(u) => { if (u?.entitled) navigate("/app"); }} />
              </div>
            ))}
            {path === "partner" && (
              <div className="fio-glass-strong fio-hairline mt-8 rounded-[32px] p-7" data-testid="partnership-panel">
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Partner with <span className="fio-gradient-text">FioNova</span></h2>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
                  FioNova can support your clients between sessions — guided reflection with Ellie, daily plans, journaling and progress they can bring back to their work with you — alongside the care they already receive. It isn't a replacement for professional treatment. Tell us about your work and we'll explore whether a partnership fits.
                </p>
                <div className="mt-6"><PartnershipForm /></div>
                <LegalNotice action="submitting an inquiry" testId="partner-legal" className="mt-4" />
              </div>
            )}
            {path === "reviewer" && (
              <div className="fio-glass-strong fio-hairline mt-8 rounded-[32px] p-7" data-testid="reviewer-panel">
                <h2 className="text-xl font-bold tracking-tight text-slate-900">Your complimentary invitation</h2>
                <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">Enter your invitation code. Then create your account or sign in to the one you already have — your journey moves into it and your 30 days of complimentary access begin.</p>
                <div className="mt-4"><InviteRedeem /></div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
